import React, { useMemo } from 'react';

import dayjs from 'utils/extendedDayJS';
import {
  SegmentedProgressChart,
  SegmentedProgressChartContainer,
  type SegmentedProgressChartData
} from '@epoch-components/Charts/SegmentedProgressChart';

import { EditEventFeedbackData } from '../../../api/feedback/getEditEventFeedbackData';
import { useEditableEvent } from '../../../hooks/useEditableEvent';

interface FeedbackProgressSectionProps {
  data: EditEventFeedbackData;
}

const chartConfig = {
  responded: {
    color: 'hsl(var(--positive-foreground))'
  },
  remaining: {
    color: 'hsl(var(--border))'
  }
};

export const FeedbackProgressSection = ({
  data
}: FeedbackProgressSectionProps): React.ReactElement => {
  const { estimatedAttendees, feedbackSchedule } = useEditableEvent();
  const { feedbackSubmitted } = data.feedbackStats;
  const { confirmed, attended } = data.guestStats;

  const isFeedbackNotificationsSent =
    !!feedbackSchedule?.sendAt && dayjs.utc(feedbackSchedule.sendAt).isBefore(dayjs.utc());

  const { baseValue, baseValueText } = useMemo(() => {
    if (estimatedAttendees && estimatedAttendees > 0) {
      return {
        baseValue: estimatedAttendees,
        baseValueText: `${estimatedAttendees} attended`
      };
    }
    if (attended > 0) {
      return {
        baseValue: attended,
        baseValueText: `${attended} attended`
      };
    }
    return {
      baseValue: confirmed || 1,
      baseValueText: `${confirmed} going`
    };
  }, [confirmed, attended, estimatedAttendees]);

  const responseRate = Math.round((feedbackSubmitted / baseValue) * 100);

  const chartData: SegmentedProgressChartData = {
    responded: feedbackSubmitted,
    remaining: baseValue - feedbackSubmitted > 0 ? baseValue - feedbackSubmitted : 0
  };

  return (
    <div className="tw-space-y-3">
      <div className="tw-flex tw-items-end tw-gap-1.5">
        <div className="tw-text-3xl tw-font-medium tw-leading-9">{responseRate}%</div>
        <div className="tw-text-base tw-leading-6">response rate</div>
      </div>
      <SegmentedProgressChartContainer config={chartConfig}>
        <SegmentedProgressChart data={chartData} barSize={8} />
      </SegmentedProgressChartContainer>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
        {feedbackSubmitted === 0 && !isFeedbackNotificationsSent ? (
          <span className="tw-leading-5 tw-text-primary-grey">
            Feedback form scheduled to be sent
          </span>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-1 tw-leading-5">
            <div className="tw-h-2 tw-w-2 tw-rounded-[2px] tw-bg-positive-foreground" />
            <span className="tw-text-positive-foreground">{feedbackSubmitted} responded</span>
          </div>
        )}
        <span className="tw-leading-5 tw-text-primary-grey">{baseValueText}</span>
      </div>
    </div>
  );
};
